import { apiFetch } from "@pontimed/api-client";
import { useSession } from "@pontimed/auth-client";
import { useQuery } from "@tanstack/react-query";
import { Navigate, Outlet, createFileRoute } from "@tanstack/react-router";

import { TwoFactorSetupWindowBanner } from "@/components/security/two-factor-setup-window-banner";

/**
 * Provider workspace layout guard. Every `/account/provider/*` page assumes
 * the session user owns a provider profile — when there is none (profile
 * never created, or deleted), deep-links and stale bookmarks would render a
 * broken workspace over 403 API responses. Redirect to the personal user
 * workspace instead, which is always valid.
 *
 * Ownership is decided by `/api/account/user/settings-data` `providerId`
 * (authoritative — reads the DB, shares the header's query cache so it's
 * usually warm). The session token deliberately carries NO `providerId`
 * claim (only `role` + active-workspace `ws`), so it can't answer this.
 */
export const Route = createFileRoute("/_authenticated/account/provider")({
  component: ProviderWorkspaceLayout,
});

interface SettingsDataLite {
  /** The user's OWN Provider row, if they have one. */
  providerId: string | null;
  /**
   * Accepted seats on someone else's practice, any role. Present since the
   * guard below stopped equating "has a provider profile" with "may open the
   * provider workspace" — a receptionist or assistant has a seat and no
   * Provider row, and was bounced to the personal dashboard with no error,
   * which left the whole team-invite flow with nowhere to land.
   */
  providerTeamSeats?: Array<{ providerId: string; role: string }>;
  isSuperadmin?: boolean;
}

function ProviderWorkspaceLayout() {
  const { status } = useSession();

  const settingsQ = useQuery({
    queryKey: ["api", "/api/account/user/settings-data"],
    queryFn: () => apiFetch.get<SettingsDataLite>("/api/account/user/settings-data"),
    enabled: status === "authenticated",
    // Entering the provider workspace re-validates against the DB even when
    // the shared cache entry is warm — a deleted profile must bounce.
    refetchOnMount: "always",
  });

  // Decide only once the authoritative DB check has resolved. Until then
  // render nothing (a warm cache keeps `data` so this is usually instant) —
  // never flash provider pages, never bounce on a stale/missing session claim.
  if (status !== "authenticated" || !settingsQ.isSuccess) return null;

  // Legacy admitted `providerId || providerTeams.length > 0 || isSuperadmin`
  // (src/lib/auth/auth.ts). The fields are optional so an apps/api that
  // predates them degrades to the old own-profile-only rule instead of
  // throwing.
  const mayEnter =
    !!settingsQ.data.providerId ||
    (settingsQ.data.providerTeamSeats?.length ?? 0) > 0 ||
    settingsQ.data.isSuperadmin === true;

  if (!mayEnter) {
    return <Navigate to="/account/user/dashboard" replace />;
  }

  // The 2FA setup-window countdown (plan D9) rides on the workspace layout so
  // it shows on every provider page during the grace period, and renders
  // nothing for everyone else.
  return (
    <>
      <TwoFactorSetupWindowBanner />
      <Outlet />
    </>
  );
}
